import { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Search, Power } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { api, apiErrorMessage } from "@/lib/api";

/**
 * Generic Master List Page dengan CRUD + search + toggle aktif.
 * Digunakan bersama oleh Cost Center dan Koperasi.
 */
export function MasterListPage({
  title,
  kicker,
  description,
  icon: Icon,
  endpoint,
  testPrefix,
  addLabel,
  emptyLabel,
  namaPlaceholder,
  kodePlaceholder,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ nama: "", kode: "", deskripsi: "", is_active: true });

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint);
      setRows(data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.nama || "").toLowerCase().includes(q) ||
      (r.kode || "").toLowerCase().includes(q) ||
      (r.deskripsi || "").toLowerCase().includes(q),
    );
  }, [rows, search]);

  const stats = useMemo(() => ({
    total: rows.length,
    aktif: rows.filter((r) => r.is_active !== false).length,
    nonaktif: rows.filter((r) => r.is_active === false).length,
  }), [rows]);

  function openCreate() {
    setEditing(null);
    setForm({ nama: "", kode: "", deskripsi: "", is_active: true });
    setOpen(true);
  }

  function openEdit(row) {
    setEditing(row);
    setForm({
      nama: row.nama || "",
      kode: row.kode || "",
      deskripsi: row.deskripsi || "",
      is_active: row.is_active !== false,
    });
    setOpen(true);
  }

  async function save() {
    if (!form.nama.trim()) {
      toast.error("Nama wajib diisi");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        nama: form.nama.trim(),
        kode: form.kode.trim() || null,
        deskripsi: form.deskripsi.trim() || null,
        is_active: !!form.is_active,
      };
      if (editing) {
        await api.put(`${endpoint}/${editing.id}`, payload);
        toast.success(`${title} berhasil diperbarui`);
      } else {
        await api.post(endpoint, payload);
        toast.success(`${title} berhasil ditambahkan`);
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  async function toggleAktif(row) {
    try {
      await api.put(`${endpoint}/${row.id}`, {
        nama: row.nama,
        kode: row.kode || null,
        deskripsi: row.deskripsi || null,
        is_active: !(row.is_active !== false),
      });
      toast.success(`${row.nama} ${row.is_active !== false ? "dinonaktifkan" : "diaktifkan"}`);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }

  async function del(row) {
    if (!confirm(`Hapus ${title} "${row.nama}"?\n\nBila masih dipakai pegawai, akan dinonaktifkan (soft-delete).`)) return;
    try {
      const { data } = await api.delete(`${endpoint}/${row.id}`);
      if (data?.soft_deleted) {
        toast.warning(data.detail || `${row.nama} dinonaktifkan`);
      } else {
        toast.success(`${row.nama} dihapus`);
      }
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto" data-testid={`page-${testPrefix}`}>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-teal-700">{kicker}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1 leading-tight flex items-center gap-3">
            {Icon ? <Icon className="text-teal-700" size={30} /> : null}
            {title}
          </h1>
          <p className="text-slate-500 text-sm mt-1 max-w-xl">{description}</p>
        </div>
        <Button
          onClick={openCreate}
          className="gap-2 bg-teal-700 hover:bg-teal-800"
          data-testid={`${testPrefix}-add-btn`}
        >
          <Plus size={16} /> {addLabel}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mt-6">
        <StatCard label="Total" value={stats.total} tone="slate" />
        <StatCard label="Aktif" value={stats.aktif} tone="emerald" />
        <StatCard label="Nonaktif" value={stats.nonaktif} tone="rose" />
      </div>

      {/* Search */}
      <div className="mt-4 relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder={`Cari ${title.toLowerCase()}...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          data-testid={`${testPrefix}-input-search`}
        />
      </div>

      {/* Table */}
      <div className="mt-4 rounded-xl border border-slate-200 bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500 w-32">Kode</TableHead>
              <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Nama</TableHead>
              <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Deskripsi</TableHead>
              <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500 w-24">Status</TableHead>
              <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500 w-32 text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-12">Memuat…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-12">{emptyLabel}</TableCell></TableRow>
            ) : filtered.map((r) => (
              <TableRow key={r.id} data-testid={`${testPrefix}-row-${r.id}`}>
                <TableCell><span className="font-mono-clock text-xs text-slate-600">{r.kode || "—"}</span></TableCell>
                <TableCell><span className="font-semibold text-slate-900">{r.nama}</span></TableCell>
                <TableCell><span className="text-slate-600 text-sm">{r.deskripsi || <span className="text-slate-300">—</span>}</span></TableCell>
                <TableCell>
                  {r.is_active !== false ? (
                    <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border border-emerald-200">Aktif</Badge>
                  ) : (
                    <Badge variant="secondary" className="bg-slate-100 text-slate-600">Nonaktif</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => toggleAktif(r)}
                      title={r.is_active !== false ? "Nonaktifkan" : "Aktifkan"}
                      data-testid={`${testPrefix}-toggle-${r.id}`}
                    >
                      <Power size={14} className={r.is_active !== false ? "text-emerald-600" : "text-slate-400"} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEdit(r)}
                      data-testid={`${testPrefix}-edit-${r.id}`}
                    >
                      <Pencil size={14} className="text-teal-700" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => del(r)}
                      data-testid={`${testPrefix}-del-${r.id}`}
                    >
                      <Trash2 size={14} className="text-rose-600" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Dialog CRUD */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`${testPrefix}-dialog`}>
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${title}` : `Tambah ${title}`}</DialogTitle>
            <DialogDescription>
              Isi minimal Nama. Kode & deskripsi opsional. Nonaktifkan bila tidak dipakai lagi
              tanpa harus menghapus histori.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Nama</Label>
              <Input
                className="mt-1.5"
                placeholder={namaPlaceholder}
                value={form.nama}
                onChange={(e) => setForm({ ...form, nama: e.target.value })}
                maxLength={120}
                data-testid={`${testPrefix}-input-nama`}
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Kode (opsional)</Label>
              <Input
                className="mt-1.5"
                placeholder={kodePlaceholder}
                value={form.kode}
                onChange={(e) => setForm({ ...form, kode: e.target.value })}
                maxLength={40}
                data-testid={`${testPrefix}-input-kode`}
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Deskripsi (opsional)</Label>
              <Textarea
                className="mt-1.5"
                placeholder="Catatan atau keterangan tambahan"
                value={form.deskripsi}
                onChange={(e) => setForm({ ...form, deskripsi: e.target.value })}
                maxLength={400}
                rows={3}
                data-testid={`${testPrefix}-input-deskripsi`}
              />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
              <div>
                <div className="text-sm font-semibold text-slate-800">Aktif</div>
                <div className="text-xs text-slate-500">
                  Hanya {title} aktif yang tampil di dropdown profil pegawai.
                </div>
              </div>
              <Switch
                checked={form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: !!v })}
                data-testid={`${testPrefix}-input-active`}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid={`${testPrefix}-cancel-btn`}>
              Batal
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-teal-700 hover:bg-teal-800"
              data-testid={`${testPrefix}-save-btn`}
            >
              {saving ? "Menyimpan…" : "Simpan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, tone }) {
  const tones = {
    slate: "border-slate-200 bg-white text-slate-900",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    rose: "border-rose-200 bg-rose-50 text-rose-800",
  };
  return (
    <div className={`rounded-xl border px-4 py-3 ${tones[tone] || tones.slate}`}>
      <div className="text-[10px] font-bold uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-2xl font-extrabold mt-0.5">{value}</div>
    </div>
  );
}
